import React from 'react';
import CardConfig from 'components/CardConfig';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import { ExpandMore } from 'screen/activitiesAndHonors/Activities';
import { formatBoolean } from 'components/helper/helper';

/** Same ✓/○ row style as Application (formatBoolean + blue/gray). */
const honorSummaryBooleanRow = (value: any, label: React.ReactNode) => {
  const on = formatBoolean(value);
  return (
    <div className="col-md-3 pl-3">
      <div className="gm-summary-field-label">
        <span
          style={{
            marginRight: '12px',
            fontSize: '18px',
            color: on ? '#1890ff' : '#d9d9d9',
            fontWeight: on ? 'bold' : 'normal',
          }}
        >
          {on ? '✓' : '○'}
        </span>
        {label}
      </div>
    </div>
  );
};

interface Props {
  data: any;
}

const HonorSum = ({ data }: Props) => {
  const [expandAll, setExpandAll] = React.useState(false);
  const [expandActive, setExpandActive] = React.useState<number>(0);

  const handleExpandAll = () => {
    setExpandAll(!expandAll);
    if (!expandAll) {
      setExpandActive(data.length);
    } else {
      setExpandActive(0);
    }
  };

  const handleExpandActive = (value: any) => {
    value
      ? setExpandActive(Math.abs(expandActive - 1))
      : setExpandActive(Math.abs(expandActive + 1));
  };

  React.useEffect(() => {
    if (expandActive === data.length) {
      setExpandAll(true);
    }

    if (expandActive === 0) {
      setExpandAll(false);
    }
  }, [expandActive, data.length, expandAll]);

  return (
    <>
      <div className="p-3">
        If you have received any honors or awards related beginning with the ninth grade or
        international equivalent, please indicate them here.
      </div>
      <div className="bg-light p-3 rounded">
        <div>App Notes:</div>
        <div>
          Common App - Allows you to report any honors related to your academic achievements
          beginning with the ninth grade or international equivalent. If you received more than
          five, please add the five most important to you.*
        </div>
        <div>UC App - Allow you to report up to 20 activities and honors combined.</div>
      </div>

      {data?.length > 0 && (
        <div className="d-flex justify-content-end my-2">
          <ExpandMore aria-expanded={expandAll} aria-label="show all" onClick={handleExpandAll}>
            {!expandAll ? <KeyboardDoubleArrowDownIcon /> : <KeyboardDoubleArrowUpIcon />}
          </ExpandMore>
        </div>
      )}

      {(data || []).map((item: any, index: number) => (
        <HonorItem
          honor={item}
          key={index}
          expandAll={expandAll}
          handleExpandActive={handleExpandActive}
        />
      ))}
    </>
  );
};

const renderFillAppStatus = (item: any) => {
  return (
    <span className="d-flex align-items-center gap-1">
      {item.isIncludeIntoCommonApp && (
        <img
          alt="cm"
          src="/assets/icons/icon-cm.svg"
          style={{ marginTop: '-8px' }}
        />
      )}
      {item.isIncludeIntoUCApp && (
        <img
          alt="uc"
          src="/assets/icons/icon-uc.svg"
          style={{ marginTop: '-8px' }}
        />
      )}
      {item.isIncludeIntoCSUApp && (
        <img
          alt="csu"
          src="./assets/icons/icon-csu-5.svg"
          style={{ marginTop: '-13px' }}
        />
      )}
    </span>
  );
};

const HonorItem = ({
  honor,
  key,
  expandAll,
  handleExpandActive,
}: {
  honor: any;
  key: number;
  expandAll?: boolean;
  handleExpandActive?: (value: any) => void;
}) => {
  return (
    <CardConfig
      expandAll={expandAll}
      handleExpandActive={handleExpandActive}
      headerElement={
        <div className="d-flex justify-content-between align-items-center w-100">
          <span className="flex-grow-1 text-truncate pe-2">
            {honor?.honorTitle || 'Honor'}
          </span>
          <span className="flex-shrink-0">
            {renderFillAppStatus(honor)}
          </span>
        </div>
      }
    >
      {/* Thông tin chính */}
      <div className="gm-summary-subsection">
        <div className="gm-summary-section-title" style={{ fontSize: 16, marginBottom: 8 }}>General Information</div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">What's the name of the award or honor? *</div>
            <div className="gm-summary-field-value">{honor?.honorTitle || '-'}</div>
            <div className="gm-summary-field-label text-end">{honor?.honorTitle?.length}/60 characters</div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Type of award or honor *</div>
            <div className="gm-summary-field-value">{honor?.typeOfHonor || '-'}</div>
          </div>
        </div>
      </div>
      {/* Grade Level — same ✓/○ format as Application */}
      <div className="gm-summary-subsection pt-3">
        <div className="gm-summary-section-title" style={{ fontSize: 16, marginBottom: 0, paddingBottom: 0 }}>Grade Level</div>
        <div className="row px-3">
          {honorSummaryBooleanRow(honor?.isGradeNinthLevel, '9')}
          {honorSummaryBooleanRow(honor?.isGradeTenthLevel, '10')}
          {honorSummaryBooleanRow(honor?.isGradeEleventhLevel, '11')}
          {honorSummaryBooleanRow(honor?.isGradeTwelvethLevel, '12')}
        </div>
      </div>
      {/* Level(s) of Recognition */}
      <div className="gm-summary-subsection pt-3">
        <div className="gm-summary-section-title" style={{ fontSize: 16, marginBottom: 0, paddingBottom: 0 }}>Level(s) of Recognition</div>
        <div className="row px-3">
          {honorSummaryBooleanRow(honor?.isSchoolLevelRecognition, 'School')}
          {honorSummaryBooleanRow(honor?.isStateLevelRecognition, 'State/Regional')}
          {honorSummaryBooleanRow(honor?.isNational, 'National')}
          {honorSummaryBooleanRow(honor?.isInternationalLevelRecognition, 'International')}
        </div>
      </div>
      {/* Mô tả */}
      <div className="gm-summary-subsection pt-3">
        <div className="gm-summary-section-title pt-4 pb-0 mb-0" style={{fontSize: 16}}>Description</div>
        <div className="gm-summary-field-grid row">
          <div className="gm-summary-field-item2 col-md-12">
            <div className="gm-summary-field-label">What are the eligibility requirements for this award or honor? *</div>
            <div className="gm-summary-field-value">{honor?.eligibilityRequirementsHonor || '-'}</div>
            <div className="gm-summary-field-label text-end d-block">{honor?.eligibilityRequirementsHonor?.length}/250 characters</div>
          </div>
          <div className="gm-summary-field-item2 col-md-12">
            <div className="gm-summary-field-label">What did you do to achieve this award or honor? *</div>
            <div className="gm-summary-field-value">{honor?.actionToAchieveHonor || '-'}</div>
            <div className="gm-summary-field-label text-end d-block">{honor?.actionToAchieveHonor?.length}/350 characters</div>
          </div>
        </div>
      </div>


      {/* Application */}
      <div className="gm-summary-subsection pt-3">
        <div className="gm-summary-section-title" style={{ fontSize: 16, marginBottom: 0, paddingBottom: 0}}>Application</div>
        <div className="row px-3">
          {honorSummaryBooleanRow(honor?.isIncludeIntoUCApp, 'UC App')}
          {honorSummaryBooleanRow(honor?.isIncludeIntoCommonApp, 'Common App')}
        </div>
      </div>
    </CardConfig>
  );
};

export default HonorSum;
